import { useSelector } from "react-redux";
import {
  getClientSelectModels,
  selectClientSelectModels,
  selectLastUpdateTime,
  selectLoading,
} from "./components/ClientSelectSlice";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import MultiSelectDropdown from "cpl-react-common/components/Inputs/MultiSelectDropdown";
import useCaching, { CachingOptions } from "cpl-react-common/Hooks/useCaching";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { useCallback, useEffect } from "react";
import { ClientSelectRequestModel } from "services/api/cplapi/CPLAPI";

type ClientSelectProps = {
  "data-test-id"?: string;
  handleChange: (selections: string[]) => void;
  defaultAll: boolean;
  defaultSome?: string[];
  disabled?: boolean;
  developmentIDs?: string[];
  blockIDs?: string[];
};

const EXPIRY_PERIOD_MS = 1000 * 60 * 30; // 30 minutes

const ClientSelectMulti: React.FC<ClientSelectProps> = (props) => {
  const loading = useSelector(selectLoading);
  const clientSelectModels = useSelector(selectClientSelectModels);
  const lastUpdateTime = useSelector(selectLastUpdateTime);
  const thunkDispatch = useThunkDispatch();

  const updateSelectModels = useCallback(() => {
    const hasBlockIDs = props.blockIDs && props.blockIDs.length > 0;
    const hasDevelopmentIDs = props.developmentIDs && props.developmentIDs.length > 0;

    const filter = new ClientSelectRequestModel({
      blockIDs: hasBlockIDs ? props.blockIDs : undefined,
      developmentIDs: !hasBlockIDs && hasDevelopmentIDs ? props.developmentIDs : undefined,
    });
    thunkDispatch(getClientSelectModels(filter));
  }, [props.blockIDs, props.developmentIDs, thunkDispatch]);

  const cachingOptions: CachingOptions = {
    lastUpdateTime,
    expiryPeriodMs: EXPIRY_PERIOD_MS,
    updateData: updateSelectModels,
    shouldUpdateOnLoad: clientSelectModels.length === 0,
  };

  useCaching(cachingOptions);

  useEffect(() => {
    updateSelectModels();
  }, [updateSelectModels]);

  return (
    <ActivityIndicator isBusy={loading}>
      <MultiSelectDropdown
        data-test-id={props["data-test-id"]}
        handleChange={(selections) => props.handleChange(selections as string[])}
        options={clientSelectModels.map((x) => ({
          value: x.value!,
          label: x.label ?? "",
        }))}
        defaultAll={props.defaultAll}
        defaultSome={props.defaultSome}
        searchable={true}
        disabled={props.disabled}
      ></MultiSelectDropdown>
    </ActivityIndicator>
  );
};

export default ClientSelectMulti;
